import React from 'react';
import { Card, CardContent, Typography, Box, alpha } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';

interface MetricCardData {
  title: string;
  value: string | number;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  icon?: React.ReactNode;
  onClick?: () => void;
  gradient?: string;
  accentColor?: string;
}

interface MetricCardProps {
  data: MetricCardData;
  compact?: boolean;
  valueFontSize?: string;
  titleFontSize?: string;
  descriptionFontSize?: string;
  height?: number;
  onDetailsClick?: (data?: any) => void;
  gridExist?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  data, 
  compact = false,
  valueFontSize = '2.125rem',
  titleFontSize = '0.875rem',
  descriptionFontSize = '0.75rem',
  height,
  onDetailsClick,
  gridExist = false
}) => {
  const { title, value, change, changeType = 'neutral', icon, onClick, gradient, accentColor } = data;
  // Modern gradient color schemes
  const gradients = {
    blue: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    purple: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    orange: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    green: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    pink: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    teal: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
  };

  const defaultGradient = gradient || gradients.blue;
  const defaultAccent = accentColor || '#667eea';

  const getChangeColor = () => {
    switch (changeType) {
      case 'positive':
        return '#10b981';
      case 'negative':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const getTrendIcon = () => {
    switch (changeType) {
      case 'positive':
        return <TrendingUpIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      case 'negative':
        return <TrendingDownIcon sx={{ fontSize: 16, mr: 0.5 }} />;
      default:
        return <TrendingFlatIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    }
  };

  return (
    <Card
      onClick={() => { 
        if (onDetailsClick) onDetailsClick(data);
        if (onClick) onClick();
      }}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        borderRadius: 4,
        background: '#ffffff',
        border: '1px solid',
        borderColor: alpha(defaultAccent, 0.1),
        height: height ? `${height}px` : 'auto',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          boxShadow: `0 20px 40px ${alpha(defaultAccent, 0.2)}`,
          transform: 'translateY(-8px)',
          cursor: gridExist ? 'pointer' : 'default',
          borderColor: alpha(defaultAccent, 0.3),
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: defaultGradient,
        },
      }}
    >
      <CardContent sx={{ 
        py: compact ? 1.5 : 3, 
        px: compact ? 2 : 3, 
        position: 'relative',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        cursor: gridExist ? 'pointer' : 'default',
        '&:last-child': {
          pb: compact ? 1.5 : 3
        }
      }}>
        {/* Icon Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: compact ? 1 : 2 }}>
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: titleFontSize,
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                mb: 0.5
              }}
            >
              {title}
            </Typography>
          </Box>
          {icon && (
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: compact ? 48 : 56,
                height: compact ? 48 : 56,
                borderRadius: 3,
                background: defaultGradient,
                boxShadow: `0 8px 16px ${alpha(defaultAccent, 0.3)}`,
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'rotate(5deg) scale(1.05)',
                },
                '& > *': {
                  color: '#ffffff !important',
                  fontSize: '28px !important',
                }
              }}
            >
              {icon}
            </Box>
          )}
        </Box>

        {/* Value Section */}
        <Typography 
          variant="h3" 
          sx={{ 
            fontSize: valueFontSize,
            fontWeight: 700,
            background: defaultGradient,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: compact ? 0.5 : 1.5,
            lineHeight: 1.2,
          }}
        >
          {value}
        </Typography>

        {/* Change Indicator */}
        {change && (
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              mt: compact ? 0.5 : 1
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                backgroundColor: alpha(getChangeColor(), 0.1),
                color: getChangeColor(),
                fontWeight: 600,
                fontSize: descriptionFontSize,
              }}
            >
              {getTrendIcon()}
              <Typography 
                component="span"
                sx={{ 
                  fontSize: descriptionFontSize,
                  fontWeight: 600,
                }}
              >
                {change}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Decorative Elements */}
        <Box
          sx={{
            position: 'absolute',
            bottom: -20,
            right: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background: alpha(defaultAccent, 0.05),
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -10,
            right: -10,
            width: 60,
            height: 60,
            borderRadius: '50%',
            background: alpha(defaultAccent, 0.03),
            pointerEvents: 'none',
          }}
        />
      </CardContent>
    </Card>
  );
};

export default MetricCard;
